class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    this.subscription = null;
  }

  // Check if push notifications are supported
  isSupported() {
    return this.isSupported;
  }

  // Check current permission status
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications not supported');
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  // Subscribe to push notifications
  async subscribe() {
    try {
      if (!this.isSupported) {
        throw new Error('Push notifications not supported');
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        return existingSubscription;
      }

      // Create new subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
      });

      this.subscription = subscription;
      console.log('Push subscription created:', subscription);
      
      // Send subscription to your backend
      await this.sendSubscriptionToBackend(subscription);
      
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      if (!this.subscription) {
        const registration = await navigator.serviceWorker.ready;
        this.subscription = await registration.pushManager.getSubscription();
      }

      if (this.subscription) {
        await this.subscription.unsubscribe();
        await this.removeSubscriptionFromBackend(this.subscription);
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  }

  // Send subscription to your backend API
  async sendSubscriptionToBackend(subscription) {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_USETOKEN}`
        },
        body: JSON.stringify({
          subscription,
          exhibitionId: this.getCurrentExhibitionId(),
          userId: this.getCurrentUserId(),
          preferences: this.getNotificationPreferences()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }

      console.log('Subscription saved to backend');
    } catch (error) {
      console.error('Error saving subscription:', error);
      // Don't throw - notification still works locally
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(subscription) {
    try {
      await fetch('/api/push/unsubscribe', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_USETOKEN}`
        },
        body: JSON.stringify({ subscription })
      });
    } catch (error) {
      console.error('Error removing subscription:', error);
    }
  }

  // Helper function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Get current exhibition ID (integrate with your context)
  getCurrentExhibitionId() {
    // This should integrate with your ExhibitionContext
    return localStorage.getItem('selectedExhibitionId') || null;
  }

  // Get current user ID (integrate with your auth)
  getCurrentUserId() {
    // This should integrate with your AuthContext
    return localStorage.getItem('userId') || 'anonymous';
  }

  // Get user notification preferences
  getNotificationPreferences() {
    return {
      eventReminders: true,
      exhibitorUpdates: true,
      scheduleChanges: true,
      generalAnnouncements: false
    };
  }

  // Test notification (for development)
  async showTestNotification() {
    if (Notification.permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      
      registration.showNotification('HELEXPO Test', {
        body: 'Push notifications are working!',
        icon: '/icon-192x192.png',
        badge: '/badge-72x72.png',
        tag: 'test-notification',
        actions: [
          {
            action: 'open',
            title: 'Open App'
          }
        ]
      });
    }
  }
}

export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;